<template>
    <div class="px-4">
        <a :name="resolvedCats[0]" :id="resolvedCats[0]"></a>
        <h3 class="justify-center text-center font-semibold mb-4">
            Build with a unified {{ headingName }} API
        </h3>

        <div class="flex flex-wrap items-start">
            <div class="w-full">
                <!-- sm:w-2/3 sm:pr-10" -->
                <p class="mb-8 text-center justify-center text-lg text-neutral-100">Read and write data from
                    multiple {{ headingName }} integrations</p>

                <div class="flex flex-wrap">
                    <div v-for="obj in objects" class="sm:pr-2 pb-2 w-full"
                        :class="objects.length < 3 ? 'sm:w-1/2' : '2xl:w-1/3 sm:w-1/2'">
                        <div class="border rounded-md">
                            <div
                                class="bg-transparent px-4 py-4 text-md border-b rounded-t-md items-center text-light-gray">
                                Unified <span class="font-semibold">{{ obj.display_name }}</span>
                                <span v-if="resolvedCats.length > 1" class="text-sm text-neutral-400 font-normal">
                                    · {{ CATEGORIES[obj.cat].name }}</span>

                                <NuxtLink
                                    :to="`https://docs.unified.to/${splitCamelCase(obj.name!)[0].toLowerCase()}/${splitCamelCase(obj.name!)[1]?.toLowerCase() ?? ''}/model`"
                                    target="_blank" class="hover:text-medium-gray">
                                    <Icon name="carbon:model-alt" class="float-right text-xl" />
                                </NuxtLink>
                            </div>
                            <div class="px-4 py-4 font-bold description">
                                <div class="font-bold text-sm">Fields:</div>
                                <div class="flex flex-wrap mt-4">
                                    <Badge
                                        v-for="field of Object.keys(obj.properties as any).filter((f) => obj.properties?.[f]?.description?.toLowerCase().includes('deprecated') !== true)"
                                        :label="field" class="mr-1 mb-1" />
                                    <!-- <span class="text-xs py-2" v-if="Object.keys(obj.properties as any).length  >8">plus {{ Object.keys(obj.properties as any).length-8 }} more fields</span> -->
                                </div>
                            </div>
                            <div class="px-4 pb-4 font-bold description">
                                <div class="font-bold text-sm">Methods:</div>
                                <div class="flex flex-wrap mt-4">
                                    <Badge v-for="method of obj.methods" :label="method" class="mr-1 mb-1" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- <div class="w-full sm:w-1/3 overflow-hidden">
                <pre class="shadow-md p-6 text-primary-green font-semibold" v-html="code"></pre>
            </div> -->
        </div>
    </div>
</template>

<script lang="ts">
import { type SwaggerDefinitions, type SwaggerPaths, capitalize } from '~/services/common';
import { SWAGGER, getDefinition } from '~/services/swagger';
import { INTEGRATION_CATEGORIES as CATEGORIES } from '~~/services/integrations';
import { splitCamelCase } from '~~/components/ApiObject.vue';

export default {
    props: {
        // integration: Object as PropType<IIntegration>,
        // desc: String,
        // code: String,
        cat: {
            type: String as PropType<keyof typeof CATEGORIES>,
            required: false,
        },
        cats: {
            type: Array as PropType<Array<keyof typeof CATEGORIES>>,
            required: false,
        },
        label: {
            type: String,
            required: false,
        },
    },
    data() {
        return {
            CATEGORIES,
        };
    },
    computed: {
        resolvedCats(): Array<keyof typeof CATEGORIES> {
            if (this.cats?.length) return this.cats;
            return this.cat ? [this.cat] : [];
        },
        headingName(): string {
            if (this.label) return this.label;
            const first = this.resolvedCats[0];
            return first ? CATEGORIES[first].name : '';
        },
        objects() {
            return this.resolvedCats.flatMap((cat) => this.loadObjects(cat));
        },
    },
    methods: {
        splitCamelCase,
        loadObjects(cat: keyof typeof CATEGORIES) {
            const _cat = cat.replace('martech', 'marketing');
            const cat_capitalized = capitalize(_cat);
            return Object.keys(SWAGGER.definitions as SwaggerDefinitions)
                .filter((c) => c.startsWith(cat_capitalized) && !c.endsWith('s'))
                .map((o) => {
                    const _o = getDefinition(o);
                    return {
                        ..._o,
                        cat,
                        display_name: o.replace(cat_capitalized, ''),
                        methods: this.get_methods(o, cat) as string[],
                        description: _o?.description,
                    };
                })
                .filter((o) => o.methods?.length > 0);
        },
        get_methods(def: string | undefined, cat: keyof typeof CATEGORIES) {
            const _cat = cat.replace('martech', 'marketing');
            const cat_capitalized = capitalize(_cat);
            const obj = def?.replace(cat_capitalized, '').toLowerCase();
            const PATHS = Object.keys(SWAGGER.paths).filter(
                (p) => (p.startsWith(`/${_cat}/`) || p.startsWith(`/${cat}/`)) && (p.endsWith(`/${obj}`) || p.includes(`/${obj}/`))
            );

            const methods = [] as string[];

            const P = SWAGGER.paths as unknown as { [path in string]: SwaggerPaths };
            PATHS.forEach((path) => {
                const p = P[path];
                const http_methods = Object.keys(p || {});
                http_methods.forEach((m) => {
                    if (m !== 'patch') {
                        methods.push(p[m].summary);
                    }
                });
            });

            return methods;
        },
    },
    // mounted() {

    // },
};
</script>

<style scoped>
pre {
    font-size: 14px;
    font-family: 'Courier New', Courier, monospace;
    background-color: #222;
    background: linear-gradient(0deg, #222, #111);
    border-radius: 8px;
    overflow-x: hidden;
    width: 100%;
}
</style>